import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, Gavel, Plus, Pencil, Trash2, ExternalLink } from 'lucide-react';
import {
  atlasApi, AtlasUser, Ocorrencia, OcorrenciaTipo, OcorrenciaStatus, LABEL_OCORRENCIA,
} from '../shared/atlasApi';
import { BadgeTipo, BadgeStatusOcorrencia } from './BadgeOcorrencia';
import ModalOcorrencia from './ModalOcorrencia';

interface Props {
  user: AtlasUser;
  podeEditar: boolean;
  onAbrirFicha?: (funccodi: number) => void;
}

function fdata(s?: string | null) {
  if (!s) return '—';
  try { return new Date(`${s.slice(0, 10)}T00:00:00`).toLocaleDateString('pt-BR'); } catch { return s; }
}

const TIPOS = Object.keys(LABEL_OCORRENCIA) as OcorrenciaTipo[];

const DisciplinarApp: React.FC<Props> = ({ user, podeEditar, onAbrirFicha }) => {
  const [itens, setItens] = useState<Ocorrencia[]>([]);
  const [resumo, setResumo] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [modal, setModal] = useState<{ aberto: boolean; editar: Ocorrencia | null }>({ aberto: false, editar: null });

  const [fTipo, setFTipo] = useState<OcorrenciaTipo | ''>('');
  const [fStatus, setFStatus] = useState<OcorrenciaStatus | ''>('');
  const [fDesde, setFDesde] = useState('');
  const [fAte, setFAte] = useState('');

  const carregar = useCallback(() => {
    setLoading(true);
    setErro(null);
    atlasApi.getOcorrencias(user, {
      tipo: fTipo || undefined,
      status: fStatus || undefined,
      desde: fDesde || undefined,
      ate: fAte || undefined,
    })
      .then(r => { setItens(r.itens || []); setResumo(r.resumo || {}); })
      .catch(e => setErro(e.message || 'Erro ao carregar'))
      .finally(() => setLoading(false));
  }, [user, fTipo, fStatus, fDesde, fAte]);

  useEffect(() => carregar(), [carregar]);

  const excluir = async (o: Ocorrencia) => {
    if (!window.confirm('Remover esta ocorrência do histórico ativo?')) return;
    try {
      await atlasApi.excluirOcorrencia(user, o._id);
      carregar();
    } catch (e: any) {
      setErro(e.message || 'Erro ao excluir');
    }
  };

  return (
    <div className="p-6 space-y-4 max-w-5xl">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="w-10 h-10 rounded-xl bg-purple-50 text-purple-600 flex items-center justify-center"><Gavel className="w-5 h-5" /></div>
        <div className="flex-1">
          <h2 className="text-lg font-bold text-gray-900">Ocorrências</h2>
          <p className="text-sm text-gray-500">Advertências, suspensões e registros formais de conduta.</p>
        </div>
        {podeEditar && (
          <button onClick={() => setModal({ aberto: true, editar: null })} className="px-4 py-2 rounded-lg bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium flex items-center gap-2">
            <Plus className="w-4 h-4" /> Nova ocorrência
          </button>
        )}
      </div>

      {/* Resumo por tipo */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {TIPOS.map(t => (
          <button
            key={t}
            onClick={() => setFTipo(fTipo === t ? '' : t)}
            className={`text-left bg-white rounded-2xl border p-4 transition ${
              fTipo === t ? 'border-purple-400 ring-2 ring-purple-100' : 'border-gray-200 hover:border-purple-300'
            }`}
          >
            <div className="text-2xl font-black text-gray-900">{resumo[t] || 0}</div>
            <div className="text-[11px] text-gray-500 leading-tight mt-0.5">{LABEL_OCORRENCIA[t]}</div>
          </button>
        ))}
      </div>

      {/* Filtros */}
      <div className="flex items-center gap-2 flex-wrap">
        <select value={fStatus} onChange={e => setFStatus(e.target.value as OcorrenciaStatus | '')} className="px-3 py-2 rounded-lg border border-gray-200 text-sm bg-white">
          <option value="">Todos os status</option>
          <option value="registrada">Registrada</option>
          <option value="ciente">Ciente</option>
          <option value="contestada">Contestada</option>
          <option value="anulada">Anulada</option>
        </select>
        <label className="text-xs text-gray-400">de</label>
        <input type="date" value={fDesde} onChange={e => setFDesde(e.target.value)} className="px-3 py-2 rounded-lg border border-gray-200 text-sm bg-white" />
        <label className="text-xs text-gray-400">até</label>
        <input type="date" value={fAte} onChange={e => setFAte(e.target.value)} className="px-3 py-2 rounded-lg border border-gray-200 text-sm bg-white" />
        {(fTipo || fStatus || fDesde || fAte) && (
          <button onClick={() => { setFTipo(''); setFStatus(''); setFDesde(''); setFAte(''); }} className="text-xs text-purple-600 hover:underline">
            limpar filtros
          </button>
        )}
        <span className="ml-auto text-xs text-gray-400">{loading ? 'Carregando...' : `${itens.length} ocorrência${itens.length === 1 ? '' : 's'}`}</span>
      </div>

      {erro && <div className="bg-rose-50 border border-rose-200 rounded-lg px-3 py-2 text-sm text-rose-700">{erro}</div>}

      {loading ? (
        <div className="p-12 text-center text-gray-400"><Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" /> Carregando...</div>
      ) : itens.length === 0 ? (
        <div className="p-12 text-center text-gray-400 bg-white rounded-2xl border border-gray-200">Nenhuma ocorrência encontrada.</div>
      ) : (
        <ul className="space-y-3">
          {itens.map(o => (
            <li key={o._id} className="bg-white rounded-2xl border border-gray-200 p-5">
              <div className="flex items-start gap-3 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <BadgeTipo tipo={o.tipo} />
                    <BadgeStatusOcorrencia status={o.status} />
                    <span className="text-xs text-gray-400">{fdata(o.data)}</span>
                  </div>
                  <button
                    onClick={() => onAbrirFicha?.(o.funccodi)}
                    disabled={!onAbrirFicha}
                    className="block text-left font-bold text-gray-900 mt-2 hover:text-purple-700 disabled:hover:text-gray-900 disabled:cursor-default"
                  >
                    {o.colaborador_nome || `Mat. ${o.funccodi}`}
                  </button>
                  <div className="text-xs text-gray-400">{[o.colaborador_cargo, o.colaborador_cc].filter(Boolean).join(' · ') || '—'}</div>
                  <div className="text-sm text-gray-800 mt-2">{o.motivo}</div>
                  {o.descricao && <p className="text-sm text-gray-600 mt-1 line-clamp-3 whitespace-pre-wrap">{o.descricao}</p>}
                  <div className="text-xs text-gray-400 mt-2 flex flex-wrap gap-x-3">
                    {o.dias_suspensao ? <span>{o.dias_suspensao} dia(s) de suspensão</span> : null}
                    {o.aplicada_por && <span>Aplicada por {o.aplicada_por}</span>}
                  </div>
                  {o.documento_url && (
                    <a href={o.documento_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-purple-600 hover:underline mt-2">
                      <ExternalLink className="w-3 h-3" /> Documento assinado
                    </a>
                  )}
                </div>
                {podeEditar && (
                  <div className="flex items-center gap-1">
                    <button onClick={() => setModal({ aberto: true, editar: o })} title="Editar" className="p-2 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-700">
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button onClick={() => excluir(o)} title="Remover" className="p-2 rounded-lg text-gray-400 hover:bg-rose-50 hover:text-rose-600">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      {modal.aberto && (
        <ModalOcorrencia
          user={user}
          ocorrencia={modal.editar}
          onClose={() => setModal({ aberto: false, editar: null })}
          onSalvo={() => { setModal({ aberto: false, editar: null }); carregar(); }}
        />
      )}
    </div>
  );
};

export default DisciplinarApp;
